<template>
    <div>
        <Head title="Edit Booking" />
        <h1 class="mb-8 text-3xl font-bold">
            <Link class="text-indigo-400 hover:text-indigo-600" href="/bookings">Biglietti</Link>
            <span class="text-indigo-400 font-medium">/</span> Edita
        </h1>
        <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
            <form @submit.prevent="update">
                <div class="flex flex-wrap -mb-8 -mr-6 p-8">
                    <text-input v-model="form.first_name" :error="form.errors.first_name" class="pb-8 pr-6 w-full lg:w-1/2" label="Nome" />
                    <text-input v-model="form.last_name" :error="form.errors.last_name" class="pb-8 pr-6 w-full lg:w-1/2" label="Cognome" />
                    <text-input v-model="form.email" :error="form.errors.email" class="pb-8 pr-6 w-full lg:w-1/2" label="Email" />
                    <text-input v-model="form.phone" :error="form.errors.phone" class="pb-8 pr-6 w-full lg:w-1/2" label="Telefono" />
                    <div class="pb-8 pr-6 w-full lg:w-1/2">
                        <label>Data</label>
                        <DatePicker v-model="form.pass_date" :masks="{ input: 'YYYY-MM-DD' }" :popover-props="{ visibility: 'click' }" locale="it">
                            <template #default="{ inputValue, inputEvents }">
                                <input
                                    :value="inputValue"
                                    class="form-input mt-2 w-full border-black"
                                    v-on="inputEvents"
                                />
                            </template>
                        </DatePicker>
                    </div>
                    <text-input disabled v-model="form.qty_adults" class="pb-8 pr-6 w-full lg:w-1/2" label="Adulti" />
                    <text-input disabled v-model="form.qty_kids" class="pb-8 pr-6 w-full lg:w-1/2" label="Bambini" />
                    <text-input disabled v-model="form.paid_amount" class="pb-8 pr-6 w-full lg:w-1/2" label="Totale pagato" />
                    <text-input disabled v-model="form.payment_status" class="pb-8 pr-6 w-full lg:w-1/2" label="Stato pagamento" />
                    <text-input disabled v-model="form.paid_at" class="pb-8 pr-6 w-full lg:w-1/2" label="Data pagamento" />
                    <text-input disabled v-model="form.validated" class="pb-8 pr-6 w-full lg:w-1/2" label="Validato" />
                    <text-input disabled v-model="form.validated_at" class="pb-8 pr-6 w-full lg:w-1/2" label="Data convalidato" />
                    <div class="pb-8 pr-6 w-full lg:w-1/2">
                        <label class="block text-sm font-medium text-gray-700">Dati stripe</label>
                        <textarea v-model="form.payment_gateway_data" disabled class="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"></textarea>
                    </div>
                    <div class="pb-8 pr-6 w-full lg:w-1/2">
                        <label class="block text-sm font-medium text-gray-700">Ombrelloni</label>
                        <textarea v-model="form.stations" disabled class="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"></textarea>
                    </div>
                </div>
                <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
                    <loading-button :loading="form.processing" class="btn-blue" type="submit">Aggiorna</loading-button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import DashboardLayout from "@/Pages/Dashboard/Shared/DashboardLayout";
import FileInput from "@/Pages/Dashboard/Shared/FileInput";
import LoadingButton from "@/Pages/Dashboard/Shared/LoadingButton";
import SelectInput from "@/Pages/Dashboard/Shared/SelectInput";
import TextInput from "@/Pages/Dashboard/Shared/TextInput";
import { Head, Link } from "@inertiajs/inertia-vue3";
import moment from "moment/moment";
import { DatePicker } from "v-calendar";

export default {
  props: {
    booking: {
      type: Object,
      required: true,
    },
  },
  components: {
    DatePicker,
    FileInput,
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
  },
  layout: DashboardLayout,
  remember: "form",
  data() {
    return {
      form: this.$inertia.form({
        first_name: this.booking.first_name || "",
        last_name: this.booking.last_name || "",
        email: this.booking.email || "",
        phone: this.booking.phone || "",
        pass_date: this.parseDate(this.booking.pass_date) || "",
      }),
    };
  },
  mounted() {
    this.initializeForm();
  },
  watch: {
    booking: {
      handler: function (newBooking) {
        this.initializeForm(newBooking);
      },
      deep: true,
    },
  },
  methods: {
    parseDate(date) {
      return date ? moment(date).format("YYYY-MM-DD") : null;
    },
    initializeForm(newBooking = this.booking) {
      this.form.first_name = String(newBooking.first_name || "");
      this.form.last_name = String(newBooking.last_name || "");
      this.form.email = String(newBooking.email || "");
      this.form.phone = String(newBooking.phone || "");
      this.form.pass_date = String(this.parseDate(newBooking.pass_date) || "");
      this.form.qty_adults = String(newBooking.qty_adults || 0);
      this.form.qty_kids = String(newBooking.qty_kids || 0);
      this.form.paid_amount = String(newBooking.paid_amount || 0);
      this.form.payment_status = String(newBooking.payment_status || "");
      this.form.paid_at = String(newBooking.paid_at || "");
      this.form.validated = String(newBooking.validated || false);
      this.form.validated_at = String(newBooking.validated_at || "");
      this.form.payment_gateway_data = JSON.stringify(
        JSON.parse(newBooking.payment_gateway_data || ""),
        null,
        2,
      );
      this.form.created_at = newBooking.created_at || "";
      this.form.stations = JSON.stringify(newBooking.stations || [], null, 2);
    },
    update() {
      const payload = {
        first_name: this.form.first_name,
        last_name: this.form.last_name,
        email: this.form.email,
        phone: this.form.phone,
        pass_date: this.parseDate(this.form.pass_date),
      };
      console.log(this.form.pass_date);
      console.log(payload);

      this.form
        .patch(`/bookings/${this.booking.id}`, payload)
        .then((res) => {
          // Handle successful response
          console.log("Booking updated successfully", res);
        })
        .catch((error) => {
          // Handle error response
          console.error("Error updating booking", error);
          // Optionally, you can provide user feedback
          alert("There was an error updating the booking. Please try again.");
        });
    },
  },
};
</script>
